import { initContract } from "@ts-rest/core";
import { z } from "zod";
const c = initContract();
/**
 * What the asset-upload endpoints accept, as file extensions (owner decision, 2026-09-02).
 *
 * Lives in the CONTRACT because it is part of what the endpoint promises, and because the web picker's `accept` and the
 * server's allow-list must not drift — a picker that offers a type the server refuses is a dead end the user only
 * discovers after choosing a file. The server remains the only real gate (a picker filter is a convenience, and
 * `accept` is trivially bypassed); an API-side test asserts the two lists are identical.
 *
 * Executable-in-a-browser types (`.html`, `.js`, …) are deliberately absent — these files are served to other people
 * from a presigned URL.
 */
export const ASSET_UPLOAD_ACCEPT_EXTENSIONS = [
    ".jpg",
    ".jpeg",
    ".png",
    ".webp",
    ".gif",
    ".svg",
    ".mp4",
    ".mov",
    ".m4v",
    ".webm",
    ".pdf",
    ".xlsx",
    ".xls",
    ".docx",
    ".doc",
    ".pptx",
    ".csv",
    ".txt",
    ".zip",
];
/** Ready for a file input's `accept` attribute. */
export const ASSET_UPLOAD_ACCEPT = ASSET_UPLOAD_ACCEPT_EXTENSIONS.join(",");
/**
 * The IMAGE subset of {@link ASSET_UPLOAD_ACCEPT_EXTENSIONS} — the single rule for "this file may be used as an
 * image", shared by the server's validation and by any picker that offers images (ADR-043 D3).
 *
 * **Why a new constant and not `mediaKind`.** There are three `mediaKind` copies in this repo (`assets.service.ts`,
 * a private one in `manuals.service.ts`, and the web's `asset-types.ts` twin) and they DISAGREE — `.bmp`/`.ico` are
 * `image` in one and `other` in another, `.ogv` likewise. Reusing any of them would import that drift into a
 * validation gate; unifying them is a behaviour change to two read paths and belongs in its own change. This list is
 * deliberately a strict subset of the upload accept list: a file the system will not accept as an upload must not
 * become an icon.
 */
export const IMAGE_UPLOAD_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".svg"];
/** True when `name`'s extension is in {@link IMAGE_UPLOAD_EXTENSIONS}. Extension-based and case-insensitive, matching
 *  how the asset browse's own `matchesTypes` filter works (so picker and server agree by construction). */
export function isImageFileName(name) {
    const lower = name.toLowerCase();
    return IMAGE_UPLOAD_EXTENSIONS.some((ext) => lower.endsWith(ext));
}
/** A file's coarse media class (mirrors the shipped `ManualMediaRef.kind`). */
export const AssetKindSchema = z.enum(["image", "video", "model3d", "audio", "other"]);
/**
 * Classify a file into its coarse media kind from its filename extension — the ONE implementation.
 *
 * **Why it lives here.** There used to be two, doing the identical job on the identical enum
 * (`ManualMediaRef.kind` and `AssetKind` are the same five values), and they had **drifted apart**: the manual read
 * classified `.bmp`/`.ico` as `image` and `.ogv` as `video`, while the asset browse called all three `other`. The
 * repo managed the duplication with a "keep the three in step" comment, which is precisely how it stopped being in
 * step. A shared function cannot disagree with itself.
 *
 * **Which lists won, and why.** The SUPERSET — because the extra entries are simply *correct*: `.bmp` and `.ico`
 * are images and `.ogv` is video, so the narrower list was mislabelling them, not deliberately excluding them. The
 * change is latent in the data either way: no `.bmp`, `.ico` or `.ogv` file exists in any realm (extension census
 * over all named files), so nothing is reclassified today — this fixes the rule before it can bite, not after.
 *
 * **What this is NOT.** It does not decide what may be UPLOADED ({@link ASSET_UPLOAD_ACCEPT_EXTENSIONS}), what may
 * be used as an icon ({@link IMAGE_UPLOAD_EXTENSIONS}), or which extensions a particular AR setting accepts
 * (`SETTING_FILE_TYPES` in the API, `MODEL_TYPES`/`MEDIA_TYPES` in the web editor). Those are ACCEPT lists — policy
 * about what is allowed — and they are deliberately separate from this, which only answers "what sort of file is
 * this?". Collapsing them together would be the opposite mistake to the one being fixed here.
 */
export function mediaKind(name) {
    const dot = name?.lastIndexOf(".") ?? -1;
    const ext = dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
    if (["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "ico"].includes(ext))
        return "image";
    if (["mp4", "m4v", "webm", "mov", "ogv"].includes(ext))
        return "video";
    if (["mp3", "m4a", "wav", "ogg"].includes(ext))
        return "audio";
    if (["glb", "gltf", "obj", "fbx"].includes(ext))
        return "model3d";
    return "other";
}
/** A child folder row. `hasChildren` (a subfolder OR a ready file) drives the picker's "disable empty leaf" rule. */
export const AssetFolderNodeSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    parentFolderId: z.number().int().nullable(),
    hasChildren: z.boolean(),
});
/** A file row. `thumbUrl`/`rawUrl` are server-presigned (content-type-signed; image-only thumbnails, ADR-024 R2-7).
 *  `selectable` = passes the active type filter (mirrors legacy `notSelectable`). */
export const AssetFileSchema = z.object({
    fileId: z.number().int(),
    name: z.string(),
    kind: AssetKindSchema,
    thumbUrl: z.string().url().nullable(),
    rawUrl: z.string().url().nullable(),
    sizeBytes: z.number().int().nullable(),
    createdAt: z.string(),
    selectable: z.boolean(),
});
/**
 * The result of a streaming asset upload (ADR-025 A2). The endpoint is a raw multipart POST (not a ts-rest route — ts-rest
 * can't model `multipart/form-data`), but the JSON RESULT is typed here so the web shares it. `status` is always `pending`:
 * the bytes are in object storage, but the thumbnail job hasn't run yet, so the file is not yet in the browse (which gates on
 * `ready`) — the user refreshes once processing completes (no polling; WS push is Epic B).
 */
export const AssetUploadResultSchema = z.object({
    fileId: z.number().int(),
    name: z.string(),
    kind: AssetKindSchema,
    /**
     * `pending` ⇒ a thumbnail job still has to run before the file appears in the library (images only).
     * `ready` ⇒ the bytes are committed and it is listed immediately — everything that gets no thumbnail (video,
     * documents, archives), because nothing else has to happen to it.
     */
    status: z.enum(["pending", "ready"]),
    sizeBytes: z.number().int().nullable(),
});
/** One breadcrumb hop (master root → … → current). */
export const AssetCrumbSchema = z.object({ id: z.number().int(), name: z.string() });
/** One level of the Master tree — the folder itself, its ancestor breadcrumb, and its immediate child folders + files. */
export const AssetFolderListingSchema = z.object({
    folder: z.object({ id: z.number().int(), name: z.string(), parentFolderId: z.number().int().nullable(), isEditable: z.boolean() }),
    breadcrumb: z.array(AssetCrumbSchema),
    folders: z.array(AssetFolderNodeSchema),
    files: z.array(AssetFileSchema),
});
/**
 * The "This manual" (project files, #217 P2) listing — the same shape as the Master listing, but `folder` is NULLABLE: a
 * migrated manual (pre-P1b-backfill) or a never-authored one has no project folder yet, so a browse returns
 * `{ folder: null, breadcrumb: [], folders: [], files: [] }` (an empty tab; the folder materialises on the first upload).
 */
export const ManualAssetFolderListingSchema = z.object({
    folder: z.object({ id: z.number().int(), name: z.string(), parentFolderId: z.number().int().nullable(), isEditable: z.boolean() }).nullable(),
    breadcrumb: z.array(AssetCrumbSchema),
    folders: z.array(AssetFolderNodeSchema),
    files: z.array(AssetFileSchema),
});
const AssetError = z.object({ message: z.string(), code: z.string() });
/**
 * Asset library browse (ADR-024 Phase 11a-3). **Master scope only** (the realm-wide "All manuals" pool). Gated on a
 * realm-level `manuals_manage` entitlement (R2-1); a requested `folderId` must be the master root or a descendant of it
 * (master-subtree containment, C1) — else 404. Lists only `status='ready'` files (H5) with content-type-signed thumbnails
 * (R2-7). Project/System scopes + write (upload/CRUD/attach) are later phases.
 */
export const assetContract = c.router({
    getAssetFolder: {
        method: "GET",
        path: "/assets/folders",
        query: z.object({
            folderId: z.coerce.number().int().positive().optional(), // omitted ⇒ the realm's master root
            types: z.string().optional(), // comma-separated extensions, e.g. ".jpg,.png,.mp4"
        }),
        responses: {
            200: AssetFolderListingSchema,
            401: z.object({ message: z.string() }),
            403: AssetError, // not a manuals_manage caller
            404: AssetError, // folder absent, or outside the master subtree
        },
        summary: "Browse the Master asset-library folder — child folders + ready files with presigned thumbnails.",
    },
    /**
     * "This manual" (project files, #217 P2). Browse the given manual's OWN project folder. Authorizes through the manual's OWN
     * edit gate (diff-panel P2-F1 — the SAME `assertManualEditable` the body edits use, NOT a looser realm/facility grant): so
     * an unassigned `manuals_manage` caller, or a linked/archived manual, is refused exactly as a body edit would be. A
     * requested `folderId` must be within the manual's `folder_id` subtree (containment, C1 — IDOR-safe). No folder yet →
     * `folder: null`.
     */
    getManualAssetFolder: {
        method: "GET",
        path: "/manuals/:manualId/assets/folders",
        pathParams: z.object({ manualId: z.coerce.number().int().positive() }),
        query: z.object({
            folderId: z.coerce.number().int().positive().optional(), // omitted ⇒ the manual's project-folder root
            types: z.string().optional(),
        }),
        responses: {
            200: ManualAssetFolderListingSchema,
            401: z.object({ message: z.string() }),
            403: AssetError, // not editable by the caller (MANUAL_FORBIDDEN — e.g. view-only, or unassigned manuals_manage)
            404: AssetError, // manual absent (MANUAL_NOT_FOUND), folder absent, or outside the manual's subtree
            409: AssetError, // the manual isn't editable: a linked child (MANUAL_IS_LINK) or archived (MANUAL_NOT_EDITABLE)
        },
        summary: "Browse a manual's own project-file folder — child folders + ready files with presigned thumbnails.",
    },
});
/** Manual-scoped streaming upload (#217 P2) — a raw multipart POST (like the Master upload, not a ts-rest route). The web
 *  builds `POST /manuals/:manualId/assets/files?folderId=<id>` and shares {@link AssetUploadResult} as the JSON result. */
export const MANUAL_ASSET_UPLOAD_PATH = (manualId) => `/manuals/${manualId}/assets/files`;
//# sourceMappingURL=asset.contract.js.map